import { notificationsApi, type ListNotificationsParams } from "@api/notifications"
import { analytics } from "@lib/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { notificationKeys } from "./queryKeys"

import { useRealtimeEvent } from "@/realtime"
import type { Notification } from "@/types"

/**
 * PRD-26 §FR-11 — list query for both the bell dropdown (limit=10)
 * and the /notificaciones page (limit=100 + filters). Real-time
 * `notification.created` events prepend to the cached list so the
 * bell + page both reflect new rows without a refetch.
 */
export const useNotifications = (params?: ListNotificationsParams) => {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
    placeholderData: (prev) => prev,
  })

  // PRD-26 §FR-14 — subscribe once per hook instance. The handler
  // prepends to EVERY notifications list cache + invalidates the
  // unread-count query. Simpler than surgical per-cache prepends
  // because the unread-count GET is < 50ms.
  useRealtimeEvent("notification.created", (payload) => {
    const fresh: Notification = {
      id: payload.id,
      notification_type: payload.notification_type,
      kind: payload.kind,
      title: payload.title,
      message: payload.message,
      link: payload.link,
      payload: payload.payload,
      read: payload.read,
      read_at: null,
      archived_at: null,
      created_at: payload.created_at,
    }
    qc.setQueriesData<Notification[]>({ queryKey: notificationKeys.lists() }, (existing) =>
      existing ? [fresh, ...existing] : [fresh],
    )
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
  })

  return query
}

/**
 * Cheap dedicated unread-count fetch the bell badge consumes. Lives
 * on its own query key so the bell never depends on the dropdown
 * being mounted.
 */
export const useUnreadCount = () => {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: notificationsApi.unreadCount,
  })

  // Same realtime hookup as `useNotifications` so the bell badge
  // updates even when the dropdown isn't open.
  useRealtimeEvent("notification.created", () => {
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
  })

  return query
}

export const useMarkRead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => {
      // The one notification interaction worth an event: it says the
      // in-app notice was actually seen. Mark-all and archive are
      // housekeeping, deliberately not tracked (PRD-57 catalogue).
      analytics.track("notification_opened")
      void qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export const useMarkAllRead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export const useArchive = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: notificationsApi.archive,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}
